"use client";

import { useState, useEffect } from "react";
import { FiTrendingDown, FiGrid, FiShare2, FiCheck } from "react-icons/fi";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { useT } from "@/hooks/useT";
import { ActivityIcon } from "@/app/components/icons/ActivityIcon";
import { TrendingUpIcon } from "@/app/components/icons/TrendingUpIcon";
import { ChevronDownIcon } from "@/app/components/icons/ChevronDownIcon";
import NetPositionsModal from "@/app/components/NetPositionsModal";
import SharePositionModal from "@/app/components/SharePositionModal";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useRedeemModalStore } from "@/stores/useRedeemModalStore";

interface Position {
  tokenId: string;
  marketStatus: string;
  winningOutcome: string | null;
  conditionId: string;
  marketSlug: string;
  marketQuestion: string;
  outcome: string;
  outcomeIndex: number;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  invested: number;
  currentValue: number;
  unrealizedPnL: number;
  realizedPnL: number;
  totalPnL: number;
  dflowTicker?: string | null;
  yesMint?: string | null;
  noMint?: string | null;
}

interface PositionsData {
  positions: Position[];
  totalPositions: number;
  totalInvested: number;
  totalCurrentValue: number;
  totalPnL: number;
}

interface MyPositionsProps {
  conditionId: string | null;
  marketSlug?: string | null;
  clobTokenIds?: string[];
  onSellClick?: (position: Position) => void;
  acceptingOrders?: boolean;
  marketQuestion?: string;
  marketImage?: string;
  outcomes?: string[];
  /** Platform source — determines which API to query */
  source?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  /** Kalshi ticker (e.g. KXLAYOFFSYINFO-26) — used when source=kalshi */
  kalshiTicker?: string;
  /** Predict.fun market ID — used when source=predict */
  predictMarketId?: string;
  /** Event-scope filter. When provided, the component fetches ALL of the
   * user's positions (no per-market server filter) and keeps only those
   * whose conditionId appears in this list. Pass every sub-market's
   * conditionId on event pages so positions across the entire event stay
   * visible while the user toggles between sub-markets — avoids the
   * flicker-on-switch caused by per-market re-fetches. */
  allowedConditionIds?: string[];
  /** Kalshi/Predict event-scope filter — same purpose as above but for
   * platforms that don't use conditionId. */
  allowedMarketSlugs?: string[];
  /** Event-scope metadata. When set, each position row renders the
   * sub-market's image + short label so the user can tell which market
   * a row belongs to without reading the conditionId. Keyed by
   * lowercased conditionId. */
  marketMetaByConditionId?: Record<string, { image?: string | null; label: string }>;
}

function isWinningPosition(position: Pick<Position, "marketStatus" | "outcome" | "winningOutcome" | "currentValue" | "shares">): boolean {
  if (position.marketStatus !== "resolved") return false;

  const normalizedOutcome = (position.outcome || "").trim().toLowerCase();
  const normalizedWinner = (position.winningOutcome || "").trim().toLowerCase();

  if (normalizedOutcome && normalizedWinner) {
    return normalizedOutcome === normalizedWinner;
  }

  return (position.currentValue ?? 0) > 0 && (position.shares ?? 0) > 0;
}

export default function MyPositions({
  conditionId,
  marketSlug,
  clobTokenIds = [],
  onSellClick,
  acceptingOrders = true,
  marketQuestion = "",
  marketImage,
  outcomes = ["Yes", "No"],
  source = "polymarket",
  kalshiTicker,
  predictMarketId,
  allowedConditionIds,
  allowedMarketSlugs,
  marketMetaByConditionId,
}: MyPositionsProps) {
  const t = useT();
  // Event-scope mode: fetch all of the user's positions and filter them
  // locally. In single-market mode the server does the filter.
  // Detect event-scope by the PROP BEING PROVIDED (not by its length) —
  // an empty allowedConditionIds is a "parent still loading its markets"
  // signal, not "single-market mode". The previous length>0 check caused
  // the component to fall through to the no-filter branch during the
  // initial render, leaking all of the user's portfolio (positions from
  // unrelated events) into the sidebar.
  const eventScope =
    Array.isArray(allowedConditionIds) || Array.isArray(allowedMarketSlugs);
  const allowedCidSet = new Set(
    (allowedConditionIds || []).map((c) => c.toLowerCase()),
  );
  const allowedSlugSet = new Set(allowedMarketSlugs || []);
  // Stable string keys for array props — bare arrays get a new reference on
  // every parent render, which would otherwise re-fire the fetch useEffect
  // once per re-render (observed as 8-10 req/s loop on /portfolio/positions
  // when crypto:price updates arrive ~1/sec and wallet is connected).
  const clobTokenIdsKey = (clobTokenIds || []).join(',');
  // Sorted stable keys so navigating between events (= different allowed set)
  // re-triggers the fetch + re-filter. Without these in deps the effect kept
  // previously-fetched positions in local state, leaking markets from other
  // events into the sidebar (e.g. US-Iran showing up inside a Movies event).
  const allowedCidsKey = Array.from(allowedCidSet).sort().join(',');
  const allowedSlugsKey = Array.from(allowedSlugSet).sort().join(',');
  const { isReady, isLoggedIn } = useAuth();
  const { walletAddress: polymarketWalletAddress, walletLoading } = usePolymarketBalanceContext();
  const [data, setData] = useState<PositionsData | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNetPositions, setShowNetPositions] = useState(false);
  const [sharePosition, setSharePosition] = useState<Position | null>(null);
  const visRef = useVisibilityRef();
  const [lastWalletUsed, setLastWalletUsed] = useState<string | null>(null);
  const [containerHovered, setContainerHovered] = useState(false);
  const [startTradingHovered, setStartTradingHovered] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const openRedeem = useRedeemModalStore((s) => s.open);

  // Build URL based on source.
  //
  // `fresh` opts the request out of the backend's PM positions Redis
  // cache. The trade-event handler below uses it on the immediate
  // refetch + retries so a stale-empty PM data-api response doesn't
  // poison the cache for the next ~5s and hide the just-bought
  // position from MyPositions. Only Polymarket benefits — Kalshi and
  // Predict use different endpoints with no equivalent cache.
  const buildPositionUrl = (bg = false, fresh = false) => {
    if (source === 'kalshi' && kalshiTicker && !eventScope) {
      return `/api/dflow/positions?ticker=${encodeURIComponent(kalshiTicker)}`;
    }
    if (source === 'kalshi' && eventScope) {
      return `/api/dflow/positions`;
    }
    if (source === 'predict' && predictMarketId && !eventScope) {
      return `/api/trading/predict/positions?marketId=${encodeURIComponent(predictMarketId)}`;
    }
    if (source === 'predict' && eventScope) {
      return `/api/trading/predict/positions`;
    }
    // Polymarket — event scope passes the event's full set of allowed
    // conditionIds (or marketSlugs) so the SERVER prunes the response
    // to this event's sub-markets before sending. The previous
    // client-side filter was racing the parent's activeMarkets
    // hydration and leaked positions from unrelated events into the
    // sidebar (e.g. "Will Scream 7..." showing inside a FIFA World Cup
    // event the user never traded). Server-side filtering removes the
    // race entirely.
    if (eventScope) {
      let url = `/api/portfolio/positions?status=open`;
      if (allowedCidsKey) url += `&conditionIds=${encodeURIComponent(allowedCidsKey)}`;
      if (allowedSlugsKey) url += `&marketSlugs=${encodeURIComponent(allowedSlugsKey)}`;
      if (polymarketWalletAddress) url += `&walletAddress=${polymarketWalletAddress}`;
      if (fresh) url += `&fresh=1`;
      return url;
    }
    let url = `/api/portfolio/positions?conditionId=${conditionId}`;
    if (marketSlug) url += `&marketSlug=${encodeURIComponent(marketSlug)}`;
    if (clobTokenIds.length > 0) url += `&tokenIds=${encodeURIComponent(clobTokenIds.join(','))}`;
    if (!acceptingOrders) url += '&status=all';
    if (polymarketWalletAddress) url += `&walletAddress=${polymarketWalletAddress}`;
    if (fresh) url += `&fresh=1`;
    return url;
  };

  // Fetch positions. For the Polymarket event-scope case the filter set
  // (conditionIds + marketSlugs for every sub-market of the event) is sent in a
  // POST body instead of the query string: a big event (e.g. a World Cup match
  // with 300+ sub-markets) produces a ~45KB URL that exceeds Cloudflare's URL
  // cap / Node's maxHeaderSize and fails with a 502. Kalshi, Predict and the
  // single-market case stay GET (short URLs). Backend reads body on POST.
  const fetchPositionsResponse = (bg = false, fresh = false): Promise<Response> => {
    if (eventScope && source !== 'kalshi' && source !== 'predict') {
      return authFetch(`/api/portfolio/positions`, {
        method: 'POST',
        body: JSON.stringify({
          status: 'open',
          ...(allowedCidsKey ? { conditionIds: allowedCidsKey } : {}),
          ...(allowedSlugsKey ? { marketSlugs: allowedSlugsKey } : {}),
          ...(polymarketWalletAddress ? { walletAddress: polymarketWalletAddress } : {}),
          ...(fresh ? { fresh: '1' } : {}),
        }),
      });
    }
    return authFetch(buildPositionUrl(bg, fresh));
  };

  useEffect(() => {
    // Local dev bail: Privy/backend auth never matches in localhost, so the
    // fetch keeps spamming 401/404. Short-circuit the effect entirely in dev.
    // Opt-in via NEXT_PUBLIC_ENABLE_LOCAL_AUTH=true.
    if (
      typeof window !== "undefined"
      && (window.location.hostname === "localhost"
        || window.location.hostname === "127.0.0.1"
        || window.location.hostname.startsWith("192.168."))
      && process.env.NEXT_PUBLIC_ENABLE_LOCAL_AUTH !== "true"
    ) {
      setInitialLoading(false);
      setData(null);
      return;
    }

    async function fetchPositions(fresh = false) {
      if (!eventScope && !conditionId && !kalshiTicker && !predictMarketId) {
        setInitialLoading(false);
        return;
      }

      // Event-scope: don't fire the fetch until the parent has finished
      // hydrating its sub-markets. Otherwise the request goes out without
      // a server-side filter, the backend returns the user's full
      // portfolio, and we'd briefly render unrelated positions before
      // the next render filters them out. Bail early — useEffect re-runs
      // when `allowedCidsKey` / `allowedSlugsKey` change.
      if (eventScope && !allowedCidsKey && !allowedSlugsKey) {
        if (data === null) {
          // First load — the parent hasn't hydrated its markets yet. Keep the
          // skeleton instead of asserting an empty result, which flashed
          // "No positions" for a beat before the real positions popped in.
          // data is null here, so there's nothing of a previous event to leak.
          setInitialLoading(true);
          return;
        }
        // We already have data (e.g. navigated to another event whose markets
        // haven't hydrated). Clear to empty so we don't leak the previous
        // event's positions while the new key resolves — the effect re-runs
        // once allowedCidsKey/allowedSlugsKey arrive.
        setInitialLoading(false);
        setData({
          positions: [],
          totalPositions: 0,
          totalInvested: 0,
          totalCurrentValue: 0,
          totalPnL: 0,
        });
        return;
      }

      // Wait for auth to be ready and user to be logged in.
      //
      // CRITICAL: distinguish "still initializing" from "genuinely logged out".
      // The previous code wiped data → null AND set initialLoading → false on
      // BOTH, so during Privy init (isReady=false) the box would render once
      // (initial initialLoading=true), then `!data && !initialLoading` flipped
      // true → the whole "My Positions" card vanished, then reappeared once
      // auth resolved and the fetch landed. Reported as "sale, se va, y sale al
      // refresh". Now we only clear on a REAL logout; while initializing we keep
      // the skeleton (don't touch data/initialLoading) so the card stays put.
      // Deps include isReady/isLoggedIn, so the effect re-runs when auth resolves.
      if (!isReady || !isLoggedIn) {
        if (isReady && !isLoggedIn) {
          // Genuine logged-out state — clear for safety.
          setData(null);
          setInitialLoading(false);
        }
        return;
      }

      // For Polymarket: wait for wallet to load. For Kalshi/Predict: skip wallet check.
      if (source === 'polymarket' && walletLoading) {
        return;
      }

      // Skip background refresh if data already loaded (Polymarket wallet-based check)
      if (source === 'polymarket' && polymarketWalletAddress === lastWalletUsed && data !== null) {
        // Background refresh - don't show loading
        try {
          const response = await fetchPositionsResponse(true, fresh);
          if (response.ok) {
            const result = await response.json();
            setData(result);
          }
        } catch {
          // Silently fail background refreshes
        }
        return;
      }

      // Initial fetch or wallet changed - show loading only if no data
      if (data === null) {
        setInitialLoading(true);
      }

      try {
        setError(null);

        const response = await fetchPositionsResponse(false, fresh);

        if (response.status === 401) {
          setData(null);
          setInitialLoading(false);
          return;
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch positions: ${response.status} ${errorText}`);
        }

        const result = await response.json();

        // Predict API returns { success, positions } — normalize to PositionsData format
        if (source === 'predict' && result.success && Array.isArray(result.positions)) {
          const filtered = result.positions
            .filter((p: any) => p.isOpen && p.size > 0.001)
            .filter((p: any) => !predictMarketId || String(p.marketId) === String(predictMarketId))
            .map((p: any) => ({
              tokenId: p.marketId,
              marketStatus: p.isResolved ? 'resolved' : 'open',
              winningOutcome: null,
              conditionId: p.marketId,
              marketSlug: p.categorySlug || p.marketSlug || '',
              marketQuestion: p.marketTitle || '',
              outcome: p.outcome || 'Yes',
              outcomeIndex: p.outcomeIndex ?? 0,
              shares: p.size || 0,
              amountRaw: p.amountRaw,
              avgPrice: p.avgPrice || 0,
              currentPrice: p.currentPrice || 0,
              invested: (p.size || 0) * (p.avgPrice || 0),
              currentValue: Number(p.valueUsd) || (p.size || 0) * (p.currentPrice || 0),
              unrealizedPnL: Number(p.pnlUsd) || 0,
              realizedPnL: 0,
              totalPnL: Number(p.pnlUsd) || 0,
            }));
          const totalInvested = filtered.reduce((s: number, p: Position) => s + p.invested, 0);
          const totalCurrentValue = filtered.reduce((s: number, p: Position) => s + p.currentValue, 0);
          const totalPnL = filtered.reduce((s: number, p: Position) => s + p.totalPnL, 0);
          setData({
            positions: filtered,
            totalPositions: filtered.length,
            totalInvested,
            totalCurrentValue,
            totalPnL,
          });
        } else if (source === 'kalshi' && result.success && Array.isArray(result.data)) {
          const filtered = result.data
            .filter((p: any) => p.size > 0.001)
            .filter((p: any) => !kalshiTicker || String(p.kalshiTicker).toUpperCase() === String(kalshiTicker).toUpperCase())
            .map((p: any) => ({
              tokenId: (p.outcome === 'yes' ? p.yesMint : p.noMint) || p.id,
              marketStatus: p.marketStatus || 'open',
              winningOutcome: p.winningOutcome || null,
              conditionId: p.kalshiTicker,
              marketSlug: p.kalshiTicker,
              marketQuestion: p.marketTitle || p.eventTitle || '',
              outcome: p.outcome === 'yes' ? 'Yes' : 'No',
              outcomeIndex: p.outcome === 'yes' ? 0 : 1,
              shares: Number(p.size) || 0,
              avgPrice: Number(p.avgPrice) || 0,
              currentPrice: Number(p.currentPrice) || 0,
              invested: (Number(p.size) || 0) * (Number(p.avgPrice) || 0),
              currentValue: Number(p.currentValue) || ((Number(p.size) || 0) * (Number(p.currentPrice) || 0)),
              unrealizedPnL: Number(p.unrealizedPnL) || 0,
              realizedPnL: 0,
              totalPnL: Number(p.unrealizedPnL) || 0,
              dflowTicker: p.dflowTicker || null,
              yesMint: p.yesMint || null,
              noMint: p.noMint || null,
            }));
          const totalInvested = filtered.reduce((s: number, p: Position) => s + p.invested, 0);
          const totalCurrentValue = filtered.reduce((s: number, p: Position) => s + p.currentValue, 0);
          const totalPnL = filtered.reduce((s: number, p: Position) => s + p.totalPnL, 0);
          setData({
            positions: filtered,
            totalPositions: filtered.length,
            totalInvested: result.totalInvested || totalInvested,
            totalCurrentValue: result.totalCurrentValue || totalCurrentValue,
            totalPnL: result.totalPnL || totalPnL,
          });
        } else {
          // Event-scope: keep only positions that belong to this event.
          //
          // Defensive filter — runs whenever eventScope is set, regardless
          // of `source`. The previous code gated on `source === 'polymarket'`
          // which was racey: when EventSidebar mounted before the event
          // payload hydrated (or `source` arrived as undefined), the filter
          // was skipped and the FULL user portfolio (closed positions from
          // every event the user ever traded) leaked into the sidebar.
          // Now we always filter when allowedCidSet has entries; if it's
          // still empty (parent still loading), we render zero rather than
          // leaking — the next render will repopulate once the event payload
          // arrives.
          if (eventScope && Array.isArray(result?.positions)) {
            const cidFilter = allowedCidSet.size > 0;
            const slugFilter = allowedSlugSet.size > 0;
            const kept = result.positions.filter((p: Position) => {
              const cid = (p.conditionId || '').toLowerCase();
              const slug = (p as any).marketSlug || '';
              if (cidFilter && cid && allowedCidSet.has(cid)) return true;
              if (slugFilter && slug && allowedSlugSet.has(slug)) return true;
              return false;
            });
            const totalInvested = kept.reduce((s: number, p: Position) => s + (p.invested || 0), 0);
            const totalCurrentValue = kept.reduce((s: number, p: Position) => s + (p.currentValue || 0), 0);
            const totalPnL = kept.reduce((s: number, p: Position) => s + (p.totalPnL || 0), 0);
            setData({
              positions: kept,
              totalPositions: kept.length,
              totalInvested,
              totalCurrentValue,
              totalPnL,
            });
          } else {
            setData(result);
          }
        }
        setLastWalletUsed(polymarketWalletAddress);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setInitialLoading(false);
      }
    }

    fetchPositions();

    // Refresh every 60 seconds (skip when tab is hidden)
    const interval = setInterval(() => { if (visRef.current) fetchPositions(); }, 60000);

    // Trade execution and position indexing are not always visible atomically.
    // Refresh immediately, then retry shortly after to catch eventual consistency.
    // Pass `fresh=true` so the backend bypasses the PM positions Redis cache:
    // a stale-empty data-api response taken right after the user's trade
    // would otherwise sit in cache and hide the new position from every
    // retry within the TTL window.
    const refreshTimeouts = new Set<ReturnType<typeof setTimeout>>();
    const handleTradeEvent = () => {
      fetchPositions(true);
      [1500, 4000].forEach((delay) => {
        const timeoutId = setTimeout(() => {
          refreshTimeouts.delete(timeoutId);
          fetchPositions(true);
        }, delay);
        refreshTimeouts.add(timeoutId);
      });
    };
    window.addEventListener("portfolio:refresh", handleTradeEvent);
    window.addEventListener("positions:refresh", handleTradeEvent);

    return () => {
      clearInterval(interval);
      refreshTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      window.removeEventListener("portfolio:refresh", handleTradeEvent);
      window.removeEventListener("positions:refresh", handleTradeEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionId, marketSlug, clobTokenIdsKey, polymarketWalletAddress, walletLoading, isReady, isLoggedIn, source, kalshiTicker, predictMarketId, allowedCidsKey, allowedSlugsKey]);

  // Derivatives (safe to compute even when data is null)
  const positionsCount = data?.positions?.length ?? 0;
  const hasPositions = positionsCount > 0;
  const isResolving = hasPositions && !acceptingOrders && data!.positions.some(p => p.marketStatus !== 'resolved');

  // While the market is "resolving" (closed but oracle hasn't pushed
  // marketStatus → 'resolved' on positions yet), the default 60s
  // refresh interval is too slow — 5m crypto up/down markets settle in
  // ~30-60s after close, but the user sat looking at "Waiting for
  // settlement" with no movement for a full minute. Fire a fast 8s
  // poll while resolving by dispatching the same `positions:refresh`
  // event the trade-execute path uses, so the existing fetch effect
  // hits with `fresh=true` (bypasses backend Redis cache).
  // MUST sit above the early-return below — React rules of hooks
  // require every hook to run on every render.
  useEffect(() => {
    if (!isResolving) return;
    const interval = setInterval(() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('positions:refresh'));
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [isResolving]);

  // Not logged in (no data, no loading, no error) - don't show anything
  if (!data && !initialLoading && !error) {
    return null;
  }
  const winningPositions = hasPositions ? data!.positions.filter(isWinningPosition) : [];
  const hasWinnings = winningPositions.length > 0;
  const totalWinnings = winningPositions.reduce((sum, p) => sum + p.currentValue, 0);
  const pnlColor = hasPositions && data!.totalPnL > 0 ? "text-[#00FF9D]" : hasPositions && data!.totalPnL < 0 ? "text-[#FF1744]" : "text-gray-900 dark:text-white";
  const pnlBg = hasPositions && data!.totalPnL > 0 ? "bg-[#00FF9D]/8" : hasPositions && data!.totalPnL < 0 ? "bg-[#FF1744]/8" : "bg-gray-50 dark:bg-[#0f0f12]";

  return (
    <div
      className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden transition-colors"
      onMouseEnter={() => setContainerHovered(true)}
      onMouseLeave={() => setContainerHovered(false)}
    >
      {/* Collapsible header — always visible, keeps layout stable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ActivityIcon size={20} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
          <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">{t("myPositions.title")}</h3>
          {hasPositions && (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white text-xs font-inter-tight font-semibold rounded-[5px]">
              {positionsCount}
            </span>
          )}
        </div>
        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          <ChevronDownIcon size={20} className="text-gray-400" isHovered={headerHovered} />
        </div>
      </button>

      <div className={`transition-[max-height,opacity] duration-200 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        {/* Loading state */}
        {initialLoading && !data && (
          <div className="px-4 pb-4 space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-24 skeleton-shimmer rounded-[5px]" style={{ animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="px-4 pb-4 font-satoshi text-sm text-[#FF1744]">{error}</p>
        )}

        {/* Empty state with CTA */}
        {!error && data && !hasPositions && (
          <div className="px-4 pb-4 flex items-center justify-between gap-3">
            <p className="font-satoshi text-xs text-gray-500 dark:text-gray-400">
              {t("myPositions.noPositions")}
            </p>
            <button
              onClick={() => window.dispatchEvent(new Event("frenflow:open-trade"))}
              onMouseEnter={() => setStartTradingHovered(true)}
              onMouseLeave={() => setStartTradingHovered(false)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#CCFF00] text-black font-inter-tight font-bold text-sm rounded-[5px] hover:bg-[#d4ff33] transition-colors active:scale-95"
            >
              <TrendingUpIcon size={16} isHovered={startTradingHovered} />
              {t("myPositions.startTrading")}
            </button>
          </div>
        )}

        {/* With positions */}
        {!error && data && hasPositions && (
          <>
      {/* Summary block */}
      <div className="px-5 pb-4">
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => setShowNetPositions(true)}
            className="flex items-center gap-1.5 font-inter-tight text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <FiGrid className="w-3 h-3" />
            {t("myPositions.net")}
          </button>
        </div>

        {/* Winner Banner */}
        {hasWinnings && (
          <div className="mb-4 flex items-center justify-between px-4 py-3 rounded-[5px] bg-[#00FF9D]/8">
            <div className="flex items-center gap-2">
              <FiCheck className="w-4 h-4 text-[#00FF9D]" />
              <span className="font-inter-tight text-sm font-medium text-[#00FF9D]">
                {t("myPositions.readyToClaim", { n: (totalWinnings ?? 0).toFixed(2) })}
              </span>
            </div>
            <button
              onClick={openRedeem}
              className="font-inter-tight px-4 py-1.5 rounded-[5px] text-xs font-bold bg-[#00FF9D] text-gray-900 hover:bg-[#00FF9D]/80 transition-colors active:scale-95"
            >
              {t("myPositions.claim")}
            </button>
          </div>
        )}

        {isResolving && !hasWinnings && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-[5px] bg-amber-500/8">
            <div className="w-1.5 h-1.5 rounded-[5px] bg-amber-500 animate-pulse" />
            <span className="font-inter-tight text-sm text-amber-600 dark:text-amber-400">
              {t("myPositions.waitingSettlement")}
            </span>
          </div>
        )}

        {/* Stats Row */}
        <div className="flex items-end justify-between">
          <div className="flex gap-5">
            <div>
              <p className="font-inter-tight text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">{t("myPositions.invested")}</p>
              <p className="font-inter-tight text-base font-semibold text-gray-900 dark:text-white tabular-nums">
                ${(data.totalInvested ?? 0).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="font-inter-tight text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">{t("myPositions.value")}</p>
              <p className="font-inter-tight text-base font-semibold text-gray-900 dark:text-white tabular-nums">
                {isResolving ? <span className="text-amber-500">--</span> : `$${(data.totalCurrentValue ?? 0).toFixed(2)}`}
              </p>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-[5px] ${pnlBg}`}>
            {isResolving ? (
              <span className="font-inter-tight text-sm font-semibold text-amber-500">{t("myPositions.resolving")}</span>
            ) : (
              <span className={`font-inter-tight text-lg font-bold tabular-nums ${pnlColor}`}>
                {(data.totalPnL ?? 0) >= 0 ? "+" : ""}{data.totalPnL?.toFixed(2) ?? "0.00"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Positions List */}
      <div className="border-t border-gray-100 dark:border-[#1A1A1A]">
        {data.positions.map((position, idx) => {
          const isYes = position.outcomeIndex === 0;
          const positionResolving = !acceptingOrders && position.marketStatus !== 'resolved';
          const pnlPercent = position.invested > 0
            ? (position.totalPnL / position.invested) * 100
            : 0;
          const isProfit = position.totalPnL > 0;
          const positionWon = isWinningPosition(position);

          // Status pill — same logic across breakpoints, factored out
          // so we don't duplicate the conditional in two layouts below.
          const StatusAction = ({ compact = false }: { compact?: boolean }) => {
            const sizeCls = compact ? "w-14 py-1 text-[11px]" : "w-full py-1.5 text-[12px]";
            if (positionWon) {
              return (
                <span className={`font-inter-tight text-center rounded-[5px] font-bold bg-[#00FF9D]/12 text-[#00FF9D] ${sizeCls}`}>
                  {t("myPositions.won")}
                </span>
              );
            }
            if (position.marketStatus === 'resolved') {
              return (
                <span className={`font-inter-tight text-center rounded-[5px] font-bold bg-gray-100 dark:bg-[#0f0f12] text-gray-400 ${sizeCls}`}>
                  {t("myPositions.lost")}
                </span>
              );
            }
            if (positionResolving) {
              return (
                <span className={`font-inter-tight text-center rounded-[5px] font-bold bg-amber-500/10 text-amber-500 ${sizeCls}`}>
                  {t("myPositions.pending")}
                </span>
              );
            }
            if (onSellClick) {
              return (
                <button
                  onClick={() => onSellClick(position)}
                  className={`font-inter-tight text-center rounded-[5px] font-bold text-[#FF1744] bg-[#FF1744]/8 hover:bg-[#FF1744] hover:text-white transition-colors active:scale-95 ${sizeCls}`}
                >
                  {t("myPositions.sell")}
                </button>
              );
            }
            return null;
          };

          const subMarketImg = (() => {
            const meta = marketMetaByConditionId?.[(position.conditionId || '').toLowerCase()];
            return meta?.image || marketImage;
          })();
          const subMarketLabel =
            marketMetaByConditionId?.[(position.conditionId || '').toLowerCase()]?.label
              || position.marketQuestion
              || '';

          return (
            <div
              key={`${position.conditionId}-${position.outcomeIndex}`}
              className={`group ${idx > 0 ? "border-t border-gray-50 dark:border-[#1A1A1A]/50" : ""}`}
            >
              {/* Desktop row — original dense layout, kept as-is. */}
              <div className="hidden md:flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors cursor-pointer">
                {/* Sub-market thumbnail (event scope only) */}
                {eventScope && subMarketImg ? (
                  <div className="relative w-7 h-7 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] flex-shrink-0">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={subMarketImg} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : null}

                {/* Outcome Badge */}
                <span className={`font-inter-tight text-[11px] font-bold px-2 py-0.5 rounded-[5px] flex-shrink-0 ${
                  isYes
                    ? "bg-[#00FF9D]/12 text-[#00FF9D]"
                    : "bg-[#FF1744]/12 text-[#FF1744]"
                }`}>
                  {position.outcome}
                </span>

                <div className="flex-1 min-w-0">
                  {eventScope ? (
                    <>
                      <p className="font-inter-tight text-xs font-semibold text-gray-900 dark:text-white truncate">
                        {subMarketLabel || '—'}
                      </p>
                      <p className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                        {position.shares.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-gray-400 dark:text-gray-500">@</span> {((position.avgPrice ?? 0) * 100).toFixed(1)}¢ · {t("myPositions.cost", { n: (position.invested ?? 0).toFixed(2) })}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-inter-tight text-sm text-gray-900 dark:text-white tabular-nums">
                        {position.shares.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-gray-400 dark:text-gray-500">@</span> {((position.avgPrice ?? 0) * 100).toFixed(1)}¢
                      </p>
                      <p className="font-inter-tight text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {t("myPositions.cost", { n: (position.invested ?? 0).toFixed(2) })}
                      </p>
                    </>
                  )}
                </div>

                <div className="text-right flex-shrink-0">
                  {positionResolving ? (
                    <p className="font-inter-tight text-sm font-medium text-amber-500">{t("myPositions.resolving")}</p>
                  ) : (
                    <>
                      <p className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                        ${(position.currentValue ?? 0).toFixed(2)}
                      </p>
                      <p className={`font-inter-tight text-[11px] font-medium tabular-nums ${
                        isProfit ? "text-[#00FF9D]" : position.totalPnL < 0 ? "text-[#FF1744]" : "text-gray-400"
                      }`}>
                        {(position.totalPnL ?? 0) >= 0 ? "+" : ""}{(position.totalPnL ?? 0).toFixed(2)} ({pnlPercent.toFixed(1)}%)
                      </p>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => setSharePosition(position)}
                    className="p-1.5 rounded-[5px] text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-gray-500 dark:hover:text-gray-400 transition-all"
                    title={t("myPositions.share")}
                  >
                    <FiShare2 className="w-3.5 h-3.5" />
                  </button>
                  <StatusAction compact />
                </div>
              </div>

              {/* Mobile card — three vertical stripes so each piece of
                  data has room to breathe. The dense row above is
                  unreadable on phones (everything truncates). Layout:
                    Row 1: thumbnail · sub-market label · outcome chip
                    Row 2: 3-column stat strip (shares · value · PnL)
                    Row 3: full-width Sell / Lost / Won / Pending button */}
              <div className="md:hidden px-4 py-3 active:bg-gray-50 dark:active:bg-[#0f0f12]/50 transition-colors">
                {/* Row 1 — header */}
                <div className="flex items-center gap-2 mb-2.5">
                  {subMarketImg ? (
                    <div className="relative w-8 h-8 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] flex-shrink-0">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={subMarketImg} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : null}
                  <p className="flex-1 min-w-0 font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2">
                    {eventScope ? (subMarketLabel || '—') : (position.marketQuestion || '—')}
                  </p>
                  <span className={`font-inter-tight text-[11px] font-bold px-2 py-0.5 rounded-[5px] flex-shrink-0 ${
                    isYes
                      ? "bg-[#00FF9D]/12 text-[#00FF9D]"
                      : "bg-[#FF1744]/12 text-[#FF1744]"
                  }`}>
                    {position.outcome}
                  </span>
                </div>

                {/* Row 2 — stat strip. Three columns: shares (size + avg
                    + cost stacked), current value, total PnL pill. Each
                    column owns its own width — never truncates. */}
                <div className="grid grid-cols-3 gap-2 mb-2.5">
                  <div>
                    <p className="font-mono text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">
                      {t("myPositions.shares")}
                    </p>
                    <p className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white tabular-nums leading-tight">
                      {position.shares.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </p>
                    <p className="font-inter-tight text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                      @ {((position.avgPrice ?? 0) * 100).toFixed(1)}¢ · ${(position.invested ?? 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">
                      {t("myPositions.value")}
                    </p>
                    {positionResolving ? (
                      <p className="font-inter-tight text-[13px] font-semibold text-amber-500 leading-tight">
                        {t("myPositions.resolving")}
                      </p>
                    ) : (
                      <p className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white tabular-nums leading-tight">
                        ${(position.currentValue ?? 0).toFixed(2)}
                      </p>
                    )}
                    <p className="font-inter-tight text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                      &nbsp;
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">
                      {t("myPositions.pnl")}
                    </p>
                    {positionResolving ? (
                      <p className="font-inter-tight text-[13px] font-semibold text-amber-500 leading-tight">—</p>
                    ) : (
                      <>
                        <p className={`font-inter-tight text-[13px] font-bold tabular-nums leading-tight ${
                          isProfit ? "text-[#00FF9D]" : position.totalPnL < 0 ? "text-[#FF1744]" : "text-gray-400"
                        }`}>
                          {(position.totalPnL ?? 0) >= 0 ? "+" : ""}${Math.abs(position.totalPnL ?? 0).toFixed(2)}
                        </p>
                        <p className={`font-inter-tight text-[10px] tabular-nums ${
                          isProfit ? "text-[#00FF9D]/70" : position.totalPnL < 0 ? "text-[#FF1744]/70" : "text-gray-400"
                        }`}>
                          {pnlPercent >= 0 ? "+" : ""}{pnlPercent.toFixed(1)}%
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Row 3 — single full-width action so the user always
                    has a tap target without competing for space with
                    the data above. */}
                <StatusAction />
              </div>
            </div>
          );
        })}
      </div>
          </>
        )}
      </div>

      {/* Modals — outside collapsible wrapper */}
      {hasPositions && data && (
        <NetPositionsModal
          isOpen={showNetPositions}
          onClose={() => setShowNetPositions(false)}
          positions={data.positions}
          marketQuestion={marketQuestion}
          marketImage={marketImage}
          outcomes={outcomes}
        />
      )}

      {sharePosition && (
        <SharePositionModal
          isOpen={!!sharePosition}
          onClose={() => setSharePosition(null)}
          position={sharePosition}
          marketQuestion={marketQuestion}
          marketImage={marketImage}
          marketSlug={sharePosition.marketSlug}
          source={source}
        />
      )}
    </div>
  );
}
